import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { useAuth } from "./AuthNewContext";
import useTrades from "../hooks/useTrades";
import useLivePrices from "../hooks/useLivePrices";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const client = axios.create({
  baseURL: `${BASE_URL}/api/ema-channel`,
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
});

const errMsg = (e) =>
  e?.response?.data?.error ||
  e?.response?.statusText ||
  e?.message ||
  "Request failed";

const SIGNALS_POLL = 5000;

// Matches STRATEGY = "ema_channel" in services/emaChannelStrategy.js
const STRATEGY_KEY = "ema_channel";
const TRADE_TYPES = ["EMA_CHANNEL_PULLBACK", "EMA_CHANNEL_MACD"];

const isEmaTrade = (t) =>
  t?.strategy === STRATEGY_KEY || TRADE_TYPES.includes(t?.trade_type);

const EmaStrategyContext = createContext(null);
export const useEmaStrategy = () => {
  const ctx = useContext(EmaStrategyContext);
  if (!ctx)
    throw new Error("useEmaStrategy must be used inside EmaStrategyProvider");
  return ctx;
};

export const EmaStrategyProvider = ({ children }) => {
  const { user } = useAuth();
  const userID = user?.userID;
  console.log("User id", userID);

  // ═══════════════════════════════════════════
  // Reuse existing infrastructure (state persists in provider)
  // ═══════════════════════════════════════════
  const trades = useTrades();
  const {
    openTrades,
    profitableTrades,
    losingTrades,
    fetchOpenTrades,
    fetchProfitableTrades,
    fetchLosingTrades,
    fetchAllClosedTrades,
    loading: tradesLoading,
    error: tradesError,
  } = trades;

  const { prices, isLive, subscribeSymbols, lastTickTime, connectionStatus } =
    useLivePrices();

  // ═══════════════════════════════════════════
  // EMA-only state
  // ═══════════════════════════════════════════
  const [watchlist, setWatchlist] = useState([]);
  const [signals, setSignals] = useState([]);
  const [emaError, setEmaError] = useState(null);

  const initialized = useRef(false);
  const signalsPollRef = useRef(null);
  const lastSubscribedKey = useRef("");

  // ═══════════════════════════════════════════
  // Loaders (axios)
  // ═══════════════════════════════════════════
  const loadWatchlist = useCallback(async () => {
    if (!userID) return [];
    try {
      const { data } = await client.get("/active", { params: { userID } });
      const list = Array.isArray(data) ? data : [];
      setWatchlist(list);
      return list;
    } catch (e) {
      console.error("[ema-ctx] loadWatchlist:", errMsg(e));
      setEmaError(errMsg(e));
      return [];
    }
  }, [userID]);

  const loadSignals = useCallback(async () => {
    if (!userID) return [];
    try {
      const { data } = await client.get("/signals/recent", {
        params: { userID, limit: 20 },
      });
      const list = Array.isArray(data) ? data : [];
      setSignals(list);
      return list;
    } catch {
      return []; // silent — poll failures shouldn't disrupt UI
    }
  }, [userID]);

  // ═══════════════════════════════════════════
  // Boot: load ONCE per session
  // ═══════════════════════════════════════════
  const loadInitial = useCallback(async () => {
    if (!userID || initialized.current) return;
    initialized.current = true;

    await Promise.all([
      loadWatchlist(),
      loadSignals(),
      fetchOpenTrades(),
      fetchProfitableTrades(),
      fetchLosingTrades(),
      fetchAllClosedTrades?.(),
    ]);
  }, [
    userID,
    loadWatchlist,
    loadSignals,
    fetchOpenTrades,
    fetchProfitableTrades,
    fetchLosingTrades,
    fetchAllClosedTrades,
  ]);

  useEffect(() => {
    if (userID) loadInitial();
  }, [userID, loadInitial]);

  const refresh = useCallback(async () => {
    initialized.current = false;
    setEmaError(null);
    await loadInitial();
  }, [loadInitial]);

  // ═══════════════════════════════════════════
  // Signals polling
  // ═══════════════════════════════════════════
  useEffect(() => {
    if (!userID) return;
    signalsPollRef.current = setInterval(loadSignals, SIGNALS_POLL);
    return () => clearInterval(signalsPollRef.current);
  }, [userID, loadSignals]);

  // ═══════════════════════════════════════════
  // Filter trades to EMA strategy
  // ═══════════════════════════════════════════
  const emaOpenTrades = useMemo(
    () => (openTrades || []).filter(isEmaTrade),
    [openTrades],
  );

  const emaClosedToday = useMemo(() => {
    const todayStr = new Date().toLocaleDateString("en-CA", {
      timeZone: "Asia/Kolkata",
    });
    const combined = [...(profitableTrades || []), ...(losingTrades || [])];
    return combined.filter((t) => {
      if (!isEmaTrade(t)) return false;
      return (
        new Date(t.entry_time).toLocaleDateString("en-CA", {
          timeZone: "Asia/Kolkata",
        }) === todayStr
      );
    });
  }, [profitableTrades, losingTrades]);

  const todaySummary = useMemo(() => {
    let total_pnl = 0,
      win = 0,
      loss = 0;
    for (const t of emaClosedToday) {
      const p = Number(t.pnl_amount) || 0;
      total_pnl += p;
      if (p > 0) win++;
      else if (p < 0) loss++;
    }
    return {
      trades: emaClosedToday,
      total_pnl: Number(total_pnl.toFixed(2)),
      open_count: emaOpenTrades.length,
      closed_count: emaClosedToday.length,
      win_count: win,
      loss_count: loss,
    };
  }, [emaClosedToday, emaOpenTrades.length]);

  // ═══════════════════════════════════════════
  // SSE subscriptions — union of watchlist + open EMA trades
  //
  // ⚠️  useLivePrices.subscribeSymbols wipes prices when called.
  //     We only call it when the symbol SET actually changes.
  // ═══════════════════════════════════════════
  useEffect(() => {
    const symbols = [
      ...watchlist.map((w) => w.symbol),
      ...emaOpenTrades.map((t) => t.symbol),
    ].filter(Boolean);

    const unique = [...new Set(symbols)].sort();
    const key = unique.join(",");
    if (key === lastSubscribedKey.current) return;
    lastSubscribedKey.current = key;

    if (unique.length > 0) subscribeSymbols(unique);
  }, [watchlist, emaOpenTrades, subscribeSymbols]);

  // ═══════════════════════════════════════════
  // Optimistic add / remove (axios)
  // ═══════════════════════════════════════════
  //   const addSymbol = useCallback(
  //     async ({ symbol, securityId, segment, instrument }) => {
  //       console.log("[addSymbol] received:", {
  //         symbol,
  //         securityId,
  //         segment,
  //         instrument,
  //       }); // ← add this
  //       if (!userID) throw new Error("Not logged in");
  //       if (watchlist.some((w) => w.symbol === symbol)) return watchlist;

  //       const optimistic = [
  //         ...watchlist,
  //         {
  //           symbol,
  //           security_id: securityId,
  //           segment,
  //           instrument,
  //           tradable: segment !== "IDX_I",
  //         },
  //       ];
  //       setWatchlist(optimistic);

  //       try {
  //         const { data } = await client.post("/active/add", {
  //           userID,
  //           symbol,
  //           security_id: securityId,
  //           segment,
  //           instrument,
  //         });
  //         setWatchlist(Array.isArray(data) ? data : optimistic);
  //         return data;
  //       } catch (e) {
  //         setWatchlist(watchlist); // rollback
  //         throw new Error(errMsg(e));
  //       }
  //     },
  //     [userID, watchlist],
  //   );
  const addSymbol = useCallback(
    async ({ symbol, securityId, segment, instrument }) => {
      console.log("[ctx-1] addSymbol called", { symbol, securityId, userID });
      if (!userID) {
        console.log("[ctx-2] NO userID, throwing");
        throw new Error("Not logged in");
      }
      if (watchlist.some((w) => w.symbol === symbol)) {
        console.log("[ctx-3] already in watchlist, returning early");
        return watchlist;
      }

      const optimistic = [
        ...watchlist,
        {
          symbol,
          security_id: securityId,
          segment,
          instrument,
          tradable: segment !== "IDX_I",
        },
      ];
      setWatchlist(optimistic);

      try {
        console.log(
          "[ctx-4] posting to",
          `${BASE_URL}/api/ema-channel/active/add`,
        );
        const { data } = await client.post("/active/add", {
          userID,
          symbol,
          security_id: securityId,
          segment,
          instrument,
        });
        console.log("[ctx-5] SUCCESS", data);
        setWatchlist(Array.isArray(data) ? data : optimistic);
        return data;
      } catch (e) {
        console.error("[ctx-6] AXIOS ERROR", {
          status: e.response?.status,
          data: e.response?.data,
          message: e.message,
          url: e.config?.url,
          baseURL: e.config?.baseURL,
        });
        setWatchlist(watchlist);
        throw new Error(errMsg(e));
      }
    },
    [userID, watchlist],
  );

  const removeSymbol = useCallback(
    async (symbol) => {
      if (!userID) return watchlist;
      const prev = watchlist;
      setWatchlist((cur) => cur.filter((w) => w.symbol !== symbol));

      try {
        const { data } = await client.post("/active/remove", {
          userID,
          symbol,
        });
        setWatchlist(
          Array.isArray(data) ? data : prev.filter((w) => w.symbol !== symbol),
        );
        return data;
      } catch (e) {
        setWatchlist(prev); // rollback
        throw new Error(errMsg(e));
      }
    },
    [userID, watchlist],
  );

  // ═══════════════════════════════════════════
  // Provider value
  // ═══════════════════════════════════════════
  const value = {
    // EMA-specific
    watchlist,
    signals,
    addSymbol,
    removeSymbol,
    refresh,

    // filtered trades
    openTrades: emaOpenTrades,
    closedToday: emaClosedToday,
    todaySummary,

    // live prices (SSE)
    prices,
    livePrices: prices, // alias for pages that expect the useTrades name
    isLive,
    connectionStatus,
    lastTickTime,

    // status
    loading: tradesLoading,
    error: emaError || tradesError,
    isReady: initialized.current,
  };

  return (
    <EmaStrategyContext.Provider value={value}>
      {children}
    </EmaStrategyContext.Provider>
  );
};
